import { ModalManager } from "@mui/material/Modal";

/**
 * Backports MUI's modern `inert`-based background hiding onto MUI 9's
 * `aria-hidden` implementation, app-wide.
 *
 * ## The bug
 *
 * MUI's `ModalManager` hides everything behind an open modal by setting
 * `aria-hidden="true"` on the background elements (the app root and any other
 * open modal/popover that is a sibling in the portal container). When
 * modal-family surfaces stack — a nested reviewer message popover over the
 * reviewer selector, a `Select`/`Menu` opened inside a dialog, a timeline/diff
 * popover over the comments pane — the lower layer keeps `aria-hidden="true"`
 * while the focus trap hands focus off between layers. During that window
 * (most visibly when the top layer closes and focus is restored/contained back
 * into the layer beneath it) the browser sees a focused element inside an
 * `aria-hidden` ancestor and logs:
 *
 *   "Blocked aria-hidden on an element because its descendant retained focus.
 *    ... Ancestor with aria-hidden: <div class='MuiPopover-root MuiModal-root
 *    ... aria-hidden='true'>."
 *
 * Chrome then drops the `aria-hidden`, which is an accessibility violation and
 * can break assistive technology.
 *
 * ## The fix
 *
 * MUI 9.2.0 exposes no prop to disable the `aria-hidden` mechanism; later MUI
 * versions resolve this exact issue by hiding background content with the
 * `inert` attribute instead. We mirror that behaviour here: whenever the manager
 * marks a container's children with `aria-hidden`, we also flip `inert` on them.
 *
 * `inert` removes the subtree from the focus order entirely, so focus can never
 * *remain* inside — and can never be *moved* into — a hidden ancestor, which is
 * precisely the condition Chrome flags. We keep `aria-hidden` in place so MUI's
 * own bookkeeping and the assistive-tech "hidden" semantics are unchanged; the
 * `inert` mirror only adds the missing focus containment. Because the background
 * behind an open modal/popover is already non-interactive to sighted users (the
 * modal backdrop swallows pointer events), there is no visual or behavioural
 * change — only the focus/accessibility defect is removed.
 *
 * The patch delegates all of the manager's real work (container tracking, scroll
 * lock, the `aria-hidden` toggling itself) to the original implementation and
 * only reconciles `inert` afterwards, so it stays correct across MUI patch
 * releases and is a no-op on browsers without `inert` support.
 */

type ModalManagerInternals = {
  containers?: ReadonlyArray<{ container: HTMLElement }>;
};

// Tracks only the elements we made inert so we never clear an `inert` that some
// other code (or a still-open lower layer) is responsible for.
const managedInertElements = new WeakSet<Element>();

/**
 * Reconcile the `inert` state of a container's direct children with the
 * `aria-hidden` state MUI's ModalManager just applied to them.
 *
 * Exported for unit testing.
 */
export function mirrorAriaHiddenToInert(container: Element): void {
  Array.prototype.forEach.call(container.children, (child: Element) => {
    const shouldHide = child.getAttribute("aria-hidden") === "true";
    const isManaged = managedInertElements.has(child);

    if (shouldHide && !isManaged) {
      managedInertElements.add(child);
      (child as HTMLElement).inert = true;
    } else if (!shouldHide && isManaged) {
      managedInertElements.delete(child);
      (child as HTMLElement).inert = false;
    }
  });
}

const PATCH_FLAG = "__attorneyHubInertBackdropPatched";

/**
 * Patch the shared MUI `ModalManager` so background content hidden with
 * `aria-hidden` is also made `inert`. Idempotent and safe to call more than once.
 */
export function enableInertModalHiding(): void {
  const prototype = ModalManager.prototype as ModalManager & Record<string, unknown>;
  if (prototype[PATCH_FLAG]) return;

  const originalAdd = prototype.add;
  const originalRemove = prototype.remove;

  prototype.add = function patchedAdd(this: ModalManager, modal, container) {
    const modalIndex = originalAdd.call(this, modal, container);
    try {
      mirrorAriaHiddenToInert(container);
    } catch {
      // An accessibility enhancement must never break modal rendering.
    }
    return modalIndex;
  };

  prototype.remove = function patchedRemove(this: ModalManager, modal, ariaHiddenState) {
    // `remove` isn't handed the container, and it may drop the tracking entry,
    // so capture the affected containers before delegating.
    const containers = ((this as unknown as ModalManagerInternals).containers ?? []).map(
      (entry) => entry.container,
    );
    const modalIndex = originalRemove.call(this, modal, ariaHiddenState);
    try {
      containers.forEach((container) => {
        if (container) mirrorAriaHiddenToInert(container);
      });
    } catch {
      // An accessibility enhancement must never break modal rendering.
    }
    return modalIndex;
  };

  prototype[PATCH_FLAG] = true;
}
